/**
 * "Find revision lines" heuristic (calibre lineage design memo §5.8, Phase 5).
 *
 * Groups one manufacturer's calibres by a normalised STEM — the name with a
 * trailing revision-like suffix removed (".2", "/1", "-1", "-B", "VK63A") — and
 * attaches the evidence a curator needs to judge each cluster: spec parity,
 * ref counts, families, and whether the suffixes are already tiers.
 *
 * Most clusters are NOT revisions (design §1.4: Glashütte 39-xx is functional
 * numbering, Panerai P.2002/x are executions, Longines is already tiered), so
 * clusters that look like tiers or peers are hidden by default and the curator
 * decides: Create line / Ask AI / Not a revision. Pure; no Firestore.
 */
import { normalizeCalibreKey } from './calibreResolution';
import { foldedAliasCandidates, type SplitCandidate } from './calibreLineageLogic';

export interface ClusterCalibre {
  id: string;
  name: string;
  familyId?: string | null;
  calibreLineageId?: string;
  deprecated?: boolean;
  referenceCount?: number;
  /** The calibre's spec object (CalibreData); read by key, so typed loosely. */
  data?: object;
}

export interface ClusterMember {
  id: string;
  name: string;
  /** The revision-like suffix, or null for the bare stem itself. */
  suffix: string | null;
  refCount: number;
  calibreLineageId?: string;
}

export type ClusterLook = 'revision' | 'tiers' | 'peers';

export interface RevisionCluster {
  /** Designation key ("vk63") — the persistence key for "Not a revision". */
  key: string;
  /**
   * Full-stem keys of the same cluster ("seiko calibre vk63") — what "Not a
   * revision" stored before clusters were keyed by designation; still honoured.
   */
  legacyKeys: string[];
  /** The stem as written on the first member, for display and as a line label seed. */
  stem: string;
  members: ClusterMember[];
  refTotal: number;
  /** Spec keys whose values differ between members that carry them. */
  differingSpecs: string[];
  familyIds: string[];
  /** A member's suffix is already a tier name on another member. */
  tierFold: boolean;
  /** Members already in some revision line. */
  alreadyLined: number;
  looksLike: ClusterLook;
}

/** Specs a revision may change (2824: 17j → 25j), so they do not argue against a line. */
const REVISABLE_SPECS = ['jewels', 'frequencyVph', 'powerReserveHours', 'shockProtection'] as const;
/** Specs a revision does not change: a difference here means peers (a different function). */
const FUNCTION_SPECS = ['isChronograph', 'movementType', 'hasDate', 'isGMT'] as const;

const SUFFIXES: RegExp[] = [
  /^(.*\d)(\.\d{1,2})$/, // 2824.2, L901.5
  /^(.*\d)(\/\d{1,2})$/, // P.2002/1
  /^(.*\d)(-\d{1,2})$/, // 2824-2, SW200-1
  /^(.*\d)(-[A-Z])$/i, // 2892-A
  // Seiko "Revision Codes" (VK63A, 6R15D, NH05B): one capital after a number
  // ending in TWO digits, so a letter that is part of the number (6R5H, 9SA5)
  // never splits. Upper-case only, so "120h" style tails don't either. A short
  // trailing qualifier ("VK64A ND", "6217A MAS") stays with the suffix.
  /^(.*\d\d)([A-Z](?:\s+[A-Z]{2,6})?)$/,
];

/**
 * The grouping key of a stem: its designation token — the last word with a
 * digit in it — so maker prefixes that differ ("SII NE15" / "SII Calibre
 * NE15C", "TMI NE57" / "Seiko NE57A") still meet. Clusters are per
 * manufacturer, so the token alone is unambiguous enough.
 */
export function designationKey(stem: string): string {
  const tokens = stem.trim().split(/\s+/);
  const token = [...tokens].reverse().find((t) => /\d/.test(t));
  return normalizeCalibreKey(token ?? stem);
}

/** Split a calibre name into stem + revision-like suffix, or null when it has none. */
export function splitRevisionSuffix(name: string): { stem: string; suffix: string } | null {
  const t = name.trim();
  for (const re of SUFFIXES) {
    const m = re.exec(t);
    if (m && m[1].trim()) return { stem: m[1].trim(), suffix: m[2] };
  }
  return null;
}

function tierNames(c: ClusterCalibre): string[] {
  const tiers = (c.data as Record<string, unknown> | undefined)?.tiers;
  if (!Array.isArray(tiers)) return [];
  return tiers
    .map((t) => (t && typeof t === 'object' ? (t as { name?: unknown }).name : undefined))
    .filter((n): n is string => typeof n === 'string')
    .map((n) => n.trim().toLowerCase());
}

const printable = (v: unknown): string | undefined =>
  typeof v === 'number' || typeof v === 'boolean' ? String(v) : typeof v === 'string' && v.trim() ? v.trim().toLowerCase() : undefined;

function differing(members: readonly ClusterCalibre[], keys: readonly string[]): string[] {
  return keys.filter((k) => {
    const vals = new Set(members.map((m) => printable((m.data as Record<string, unknown> | undefined)?.[k])).filter((v): v is string => v !== undefined));
    return vals.size > 1;
  });
}

/**
 * Candidate revision clusters among one manufacturer's calibres. Deprecated
 * stubs are ignored; a cluster needs two or more members, and one whose members
 * already all sit in the same line is finished, so it is left out.
 */
export function detectRevisionClusters(calibres: readonly ClusterCalibre[]): RevisionCluster[] {
  const live = calibres.filter((c) => !c.deprecated && c.name.trim());
  const groups = new Map<
    string,
    { stem: string; legacy: Set<string>; items: Array<{ c: ClusterCalibre; suffix: string | null }> }
  >();
  const add = (key: string, stem: string, c: ClusterCalibre, suffix: string | null) => {
    const g = groups.get(key) ?? { stem, legacy: new Set<string>(), items: [] };
    g.legacy.add(normalizeCalibreKey(stem));
    if (!g.items.some((i) => i.c.id === c.id)) g.items.push({ c, suffix });
    groups.set(key, g);
  };
  const stemKeys = new Set<string>();
  for (const c of live) {
    const split = splitRevisionSuffix(c.name);
    if (split) {
      const key = designationKey(split.stem);
      stemKeys.add(key);
      add(key, split.stem, c, split.suffix);
    }
  }
  // The bare original ("ETA 2824") joins its stem's group with no suffix.
  for (const c of live) {
    const key = designationKey(c.name);
    if (stemKeys.has(key) && !splitRevisionSuffix(c.name)) add(key, c.name.trim(), c, null);
  }

  const out: RevisionCluster[] = [];
  for (const [key, g] of groups) {
    if (g.items.length < 2) continue;
    const lines = new Set(g.items.map((i) => i.c.calibreLineageId ?? ''));
    if (lines.size === 1 && !lines.has('')) continue;

    const cs = g.items.map((i) => i.c);
    const functionDiff = differing(cs, FUNCTION_SPECS);
    const differingSpecs = [...functionDiff, ...differing(cs, REVISABLE_SPECS)];
    const allTiers = new Set(cs.flatMap(tierNames));
    const tierFold = g.items.some((i) => {
      if (!i.suffix) return false;
      const bare = i.suffix.replace(/^[-./]/, '').toLowerCase();
      return allTiers.has(bare) || allTiers.has(i.suffix.toLowerCase());
    });
    const looksLike: ClusterLook = tierFold ? 'tiers' : functionDiff.length ? 'peers' : 'revision';
    const members: ClusterMember[] = g.items
      .map(({ c, suffix }) => ({
        id: c.id,
        name: c.name,
        suffix,
        refCount: typeof c.referenceCount === 'number' ? c.referenceCount : 0,
        ...(c.calibreLineageId ? { calibreLineageId: c.calibreLineageId } : {}),
      }))
      .sort((a, b) => (a.suffix === null ? -1 : b.suffix === null ? 1 : a.name.localeCompare(b.name, undefined, { numeric: true })));
    out.push({
      key,
      legacyKeys: [...g.legacy].filter((k) => k !== key),
      stem: g.stem,
      members,
      refTotal: members.reduce((n, m) => n + m.refCount, 0),
      differingSpecs,
      familyIds: [...new Set(cs.map((c) => c.familyId).filter((f): f is string => !!f))],
      tierFold,
      alreadyLined: members.filter((m) => m.calibreLineageId).length,
      looksLike,
    });
  }
  // Likely revisions first, then by how many refs the decision touches.
  return out.sort((a, b) => {
    if ((a.looksLike === 'revision') !== (b.looksLike === 'revision')) return a.looksLike === 'revision' ? -1 : 1;
    return b.refTotal - a.refTotal || a.stem.localeCompare(b.stem);
  });
}

/** True when the curator marked this cluster "Not a revision" (under its current or a legacy key). */
export function isDismissedCluster(c: Pick<RevisionCluster, 'key' | 'legacyKeys'>, dismissed: ReadonlySet<string>): boolean {
  return dismissed.has(c.key) || c.legacyKeys.some((k) => dismissed.has(k));
}

export interface FoldedRevisionCodes {
  id: string;
  name: string;
  codes: SplitCandidate[];
}

/**
 * Calibres whose ALIASES carry their own Revision Codes ("Seiko Calibre 6R15"
 * with AKA "6R15D"). Find revision lines clusters names only, so these never
 * form a group until the codes are split out into their own calibres (the
 * Revisions card's "Split folded aliases" tool).
 */
export function foldedRevisionCodes(calibres: readonly (ClusterCalibre & { alsoKnownAs?: string[] })[]): FoldedRevisionCodes[] {
  return calibres
    .filter((c) => !c.deprecated && c.name.trim())
    .map((c) => ({ id: c.id, name: c.name, codes: foldedAliasCandidates(c.name, c.alsoKnownAs ?? []) }))
    .filter((f) => f.codes.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
}

/**
 * The calibre NUMBER of a name, without its revision suffix ("Seiko Calibre
 * 6R15D" → "6R15", "ETA 2824-2" → "2824"). One number = one revision line.
 */
export function calibreNumber(name: string): string {
  const stem = splitRevisionSuffix(name)?.stem ?? name;
  const tokens = stem.trim().split(/\s+/);
  return ([...tokens].reverse().find((t) => /\d/.test(t)) ?? stem.trim()).toUpperCase();
}

/**
 * The distinct calibre numbers among these names when there is more than one
 * ("6R15", "6R35"), else []. A line holding two numbers merges calibres that
 * replaced each other, which are separate revision lines.
 */
export function mixedCalibreNumbers(names: readonly string[]): string[] {
  const numbers = [...new Set(names.map(calibreNumber))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  return numbers.length > 1 ? numbers : [];
}

export const mixedNumbersText = (numbers: readonly string[]) =>
  `${numbers.join(', ')} are different calibre numbers. Each number is its own revision line; a successor number belongs in the line's description ("Replaced in production by …"), not as a member.`;
